// FILE: frontend/src/CoreApp.jsx
// ESL Web Demo — Main orchestrator (state, WebSocket, audio)
// Rendering delegated to components/ — see components/ for UI.
//
// This component assumes the user is already authenticated.
// The parent must provide an async token getter and a logout handler.

import React, { useState, useEffect, useRef } from 'react'
import { floatTo16BitPCM, downsampleTo24k, generateSessionId } from './utils/audio'
import NavBar from './components/NavBar'
import LessonSelect from './components/LessonSelect'
import ConversationView from './components/ConversationView'
import CompleteView from './components/CompleteView'
import ReviewView from './components/ReviewView'
import './styles/theme.css'

export default function CoreApp({ getAuthToken, onLogout }) {
  console.log('RUNNING APP FROM: CoreApp.jsx (refactored)')

  // ========== ALL HOOKS MUST BE DECLARED FIRST ==========

  // ---------- UI / app state ----------
  const [view, setView] = useState('select')
  const [lessons, setLessons] = useState([])
  const [lessonSource, setLessonSource] = useState('api')
  const [selectedLesson, setSelectedLesson] = useState(null)
  const [selectedLevel, setSelectedLevel] = useState('A2')
  const [mode, setMode] = useState('practice')

  // TEMP: disable placement tests
  useEffect(() => {
    if (mode === 'test') setMode('practice')
  }, [mode])

  // Push-to-talk state
  const [uiConfig, setUiConfig] = useState(null)
  const [micActive, setMicActive] = useState(false)

  // Connection guard (UI feedback)
  const [isConnecting, setIsConnecting] = useState(false)

  // Sync PTT refs so audio callback sees current values
  const micActiveRef = useRef(false)
  const uiConfigRef = useRef(null)
  useEffect(() => { micActiveRef.current = micActive }, [micActive])
  useEffect(() => { uiConfigRef.current = uiConfig }, [uiConfig])

  const [transcript, setTranscript] = useState([])
  const [scores, setScores] = useState([])
  const [goals, setGoals] = useState({ completed: 0, total: 0 })

  // Unified timeline for interleaved chat rendering
  // Entries: { type: 'assistant'|'user'|'score'|'goal', ... }
  const [timeline, setTimeline] = useState([])
  const [goalKeys, setGoalKeys] = useState([])
  const [serverExchanges, setServerExchanges] = useState(null)

  // Placement test state
  const [testScores, setTestScores] = useState([])
  const [placedLevel, setPlacedLevel] = useState(null)

  const [assistantTurnText, setAssistantTurnText] = useState('')
  const lastSpeakerRef = useRef(null)
  const lastUserTextRef = useRef('')
  const userTurnsRef = useRef([])
  const newAssistantTurnRef = useRef(true) // signals timeline to start a new AI bubble
  const [reviewIdx, setReviewIdx] = useState(0)

  const [isRecording, setIsRecording] = useState(false)
  const [error, setError] = useState(null)

  // ---------- refs ----------
  const wsRef = useRef(null)
  const audioContextRef = useRef(null)
  const streamRef = useRef(null)
  const processorRef = useRef(null)
  const isRecordingRef = useRef(false)

  // Synchronous connection lock — immune to React state batching races
  const isConnectingRef = useRef(false)

  const audioQueueRef = useRef([])
  const isPlayingRef = useRef(false)
  const nextPlayTimeRef = useRef(0)

  const lastAssistantAudioAtRef = useRef(0)
  const assistantAudioHoldMsRef = useRef(900)
  const assistantAudioDoneRef = useRef(false)

  const endRequestedRef = useRef(false)
  const pendingViewAfterDrainRef = useRef(null)

  // Session generation counter.
  // Each new WS session increments this and captures the value in a closure.
  // All four WS callbacks check their captured value against the current one
  // and bail if stale. This prevents a late server-side close from a finished
  // session calling stopRecording() on a new session's AudioContext — which
  // caused the "Start Practice does nothing" hang.
  const sessionGenRef = useRef(0)

  // ---------- ALL useEffect hooks ----------

  // Load lessons on mount
  useEffect(() => {
    let cancelled = false
    if (lessonSource !== 'api') return

    ;(async () => {
      try {
        const token = await getAuthToken()
        // Clear Server-side cache so new Drive Lessons appear immediately
        await fetch('/api/lessons/refresh',{
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {})
        const res = await fetch('/api/lessons', {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!res.ok) throw new Error(`Lessons request failed: ${res.status}`)
        const data = await res.json()
        if (!cancelled) {
          const lessonList = data.lessons || []
          setLessons(lessonList)
          if (lessonList.length > 0) setSelectedLesson(lessonList[0].id)
        }
      } catch (err) {
        console.error('Failed to load lessons:', err)
        if (!cancelled) setError('Failed to load lessons from server.')
      }
    })()

    return () => { cancelled = true }
  }, [getAuthToken, lessonSource])

  // ========== FUNCTIONS (after all hooks) ==========

  const getSelectedLessonObj = () => lessons.find(l => l.id === selectedLesson)

  const handleLogout = () => {
    try { if (wsRef.current) wsRef.current.close() } catch (e) { console.warn('WS close error:', e) }
    try { stopRecording() } catch (e) { console.warn('Stop recording error:', e) }
    try { onLogout() } catch (e) { console.warn('Logout handler error:', e) }
  }

  // ---------- start conversation ----------
  const startConversation = async () => {
    // ── Prevent duplicate sessions (mobile double-tap, slow mic permission) ──
    // Use ref for synchronous lock (React state batching can't race this)
    if (isConnectingRef.current) return
    if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) {
      console.warn('⚠️ Session already active, ignoring duplicate start')
      return
    }
    isConnectingRef.current = true
    setIsConnecting(true)

    if (mode === 'test') {
      setError('Placement tests are temporarily disabled.')
      isConnectingRef.current = false
      setIsConnecting(false)
      return
    }

    // Capacity check
    try {
      const token = await getAuthToken()
      const capRes = await fetch('/api/capacity', { headers: { Authorization: `Bearer ${token}` } })
      if (capRes.ok) {
        const cap = await capRes.json()
        if (cap.at_capacity) {
          setError(`Server is full (${cap.current_sessions}/${cap.max_sessions} users). Try again shortly.`)
          isConnectingRef.current = false
          setIsConnecting(false)
          return
        }
      }
    } catch (e) { console.warn('Capacity check failed:', e) }

    // Locked lesson guard
    if (mode === 'practice' && lessonSource === 'api') {
      const sel = getSelectedLessonObj()
      if (sel?.locked) {
        setError(`Lesson attempt limit reached (${sel.attempts_used ?? 3}/${sel.attempts_limit ?? 3}).`)
        isConnectingRef.current = false
        setIsConnecting(false)
        return
      }
    }

    try {
      setError(null)

      // Clean up any lingering WebSocket from a previous session
      if (wsRef.current) {
        try { wsRef.current.close() } catch (e) { /* ignore */ }
        wsRef.current = null
      }

      // Reset all state
      setTranscript([])
      setScores([])
      setGoals({ completed: 0, total: 0 })
      setGoalKeys([])
      setTimeline([])
      setServerExchanges(null)
      setAssistantTurnText('')
      lastSpeakerRef.current = null
      lastUserTextRef.current = ''
      userTurnsRef.current = []
      newAssistantTurnRef.current = true
      audioQueueRef.current = []
      isPlayingRef.current = false
      nextPlayTimeRef.current = 0
      assistantAudioDoneRef.current = false
      endRequestedRef.current = false
      pendingViewAfterDrainRef.current = null
      setUiConfig(null)
      setMicActive(false)
      setTestScores([])
      setPlacedLevel(null)

      // Mic with echo cancellation
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, sampleRate: 24000 }
      })
      streamRef.current = stream

      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      audioContextRef.current = audioContext
      console.log(`🎧 AudioContext at ${audioContext.sampleRate} Hz`)

      const token = await getAuthToken()
      const sessionId = generateSessionId()
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const host = window.location.host || 'localhost:8000'
      const wsPath = mode === 'test' ? 'ws-test' : 'ws'
      const wsUrl = `${protocol}//${host}/${wsPath}/${sessionId}?token=${token}`

      // Bump generation BEFORE attaching handlers. Each handler closes over
      // myGen and returns immediately if sessionGenRef.current !== myGen.
      const myGen = ++sessionGenRef.current
      console.log(`🔑 Starting session generation ${myGen}`)

      const ws = new WebSocket(wsUrl)
      ws.binaryType = 'arraybuffer'
      wsRef.current = ws

      ws.onopen = () => {
        if (sessionGenRef.current !== myGen) { console.warn(`onopen: stale gen ${myGen}, ignoring`); return }
        console.log('✅ WebSocket connected')
        const init = mode === 'test'
          ? { type: 'init', mode: 'test' }
          : { type: 'init', lesson: selectedLesson, level: selectedLevel }
        ws.send(JSON.stringify(init))
      }

      ws.onmessage = async (event) => {
        if (sessionGenRef.current !== myGen) return
        if (event.data instanceof ArrayBuffer) { handleIncomingPcm(event.data); return }
        if (event.data instanceof Blob) { handleIncomingPcm(await event.data.arrayBuffer()); return }
        try {
          const msg = JSON.parse(event.data)
          if (!window.__msgCounts) window.__msgCounts = {}
          window.__msgCounts[msg.type] = (window.__msgCounts[msg.type] || 0) + 1
          if (msg.type === 'error') {
            console.error('❌ SERVER ERROR:', msg.message || msg)
            setError(`Server error: ${msg.message || 'Unknown error'}`)
            return
          }
          handleWebSocketMessage(msg)
        } catch (e) { console.warn('Non-JSON WS data:', e) }
      }

      ws.onerror = () => {
        if (sessionGenRef.current !== myGen) return
        setError('Connection error. Check server logs.')
      }

      ws.onclose = (event) => {
        // Stale session — do nothing. A late server-side close must not call
        // stopRecording() on a new session's AudioContext, which is what was
        // causing the "Start Practice does nothing" hang.
        if (sessionGenRef.current !== myGen) {
          console.log(`onclose: stale gen ${myGen} (current ${sessionGenRef.current}), ignoring`)
          return
        }
        if (event.code === 1008 && event.reason?.includes('capacity')) {
          setError('Server at max capacity. Try again shortly.')
        } else if (event.code === 1011) {
          setError('Server config error. Check OPENAI_API_KEY.')
        } else if (event.code === 1008) {
          setError('Auth error. Try logging in again.')
        }
        if (endRequestedRef.current) { stopMicCapture(); maybeFinalizeAfterDrain(); return }
        stopRecording()
        audioQueueRef.current = []
        isPlayingRef.current = false
      }

      isConnectingRef.current = false
      setIsConnecting(false)
      setView('conversation')
    } catch (err) {
      console.error('Failed to start:', err)
      setError('Failed to access microphone. Check permissions.')
      isConnectingRef.current = false
      setIsConnecting(false)
    }
  }

  // ---------- WS message handler ----------
  const handleWebSocketMessage = (message) => {
    switch (message.type) {
      case 'error': {
        if (message.error_code === 'CAPACITY_REACHED') {
          setError(`Server full (${message.current_users}/${message.max_users}).`)
          if (view === 'conversation') endConversation()
          return
        }
        if (message.error_code === 'LESSON_LIMIT_REACHED') {
          setError(`Lesson limit reached (${message.used ?? 3}/${message.limit ?? 3}).`)
          requestGracefulEnd('select')
          return
        }
        setError(message.message || 'Unknown server error.')
        return
      }

      case 'summary': {
        if (message.mode === 'test') {
          if (message.placed_level) setPlacedLevel(String(message.placed_level))
          const isFinal = Boolean(message.final) || Boolean(message.placed_level?.toString().trim())
          if (isFinal) requestGracefulEnd('complete')
          break
        }
        setServerExchanges(Number(message.exchanges ?? transcript.filter(m => m.speaker === 'user').length))
        setGoals(prev => ({
          completed: message.goals_completed ?? prev.completed,
          total: message.goals_total ?? prev.total
        }))
        requestGracefulEnd('complete')
        break
      }

      case 'assistant_audio_done': {
        assistantAudioDoneRef.current = true
        maybeFinalizeAfterDrain()
        break
      }

      case 'assessment_complete': {
        const lvl = String(message.placed_level || '').trim()
        if (lvl) setPlacedLevel(lvl)
        requestGracefulEnd('complete')
        break
      }

      case 'test_score': {
        setTestScores(prev => [...prev, {
          level: String(message.level || ''),
          adequate: Boolean(message.adequate),
          overall: Number(message.overall ?? 0),
          fluency: Number(message.fluency ?? 0),
          completeness: Number(message.completeness ?? 0),
          vocabulary: Number(message.vocabulary ?? 0),
          grammar: Number(message.grammar ?? 0),
          feedback: String(message.feedback ?? ''),
          reasons: Array.isArray(message.reasons) ? message.reasons : []
        }])
        break
      }

      case 'ready': {
        console.log('Session ready')
        if (message.goals_total !== undefined) {
          setGoals({ completed: message.goals_completed || 0, total: message.goals_total })
        }
        if (message.goal_keys) setGoalKeys(message.goal_keys)
        if (message.ui_config) {
          setUiConfig(message.ui_config)
          console.log('UI config:', message.ui_config)
        }
        startAudioCapture()
        break
      }

      // Robust assistant text handling
      case 'assistant':
      case 'assistant_text':
      case 'assistant_text_delta':
      case 'assistant_text_partial':
      case 'assistant_text_full':
      case 'assistant_text_complete':
      case 'assistant_say':
      case 'assistant_message': {
        const text = String(message.text ?? '').trim()
        if (!text) break
        // Safety net: filter leaked system directives
        if (/INTERNAL DIRECTIVE|DO NOT READ THIS ALOUD/i.test(text)) {
          console.warn('🚫 Filtered leaked system prompt:', text.substring(0, 80))
          break
        }
        const isFinalish = /complete|full|final/i.test(message.type)
        setAssistantTurnText(prev => {
          if (!prev) return text
          return isFinalish ? `${prev}\n\n${text}` : `${prev} ${text}`
        })
        // Timeline: append to current AI bubble or start new one
        setTimeline(prev => {
          if (!newAssistantTurnRef.current && prev.length > 0 && prev[prev.length - 1].kind === 'assistant') {
            const updated = [...prev]
            const last = { ...updated[updated.length - 1] }
            last.text = last.text + (isFinalish ? '\n' : ' ') + text
            updated[updated.length - 1] = last
            return updated
          }
          newAssistantTurnRef.current = false
          return [...prev, { kind: 'assistant', text }]
        })
        lastSpeakerRef.current = 'assistant'
        break
      }

      case 'transcript': {
        const who = String(message.speaker || '').toLowerCase()
        const text = String(message.text ?? '').trim()
        if (!text) break
        if (who === 'assistant') {
          const sep = lastSpeakerRef.current === 'assistant' ? ' ' : '\n\n'
          setAssistantTurnText(prev => prev ? `${prev}${sep}${text}` : text)
          // Timeline: append to current AI bubble or start new one
          const isNewTurn = lastSpeakerRef.current !== 'assistant'
          setTimeline(prev => {
            if (!isNewTurn && !newAssistantTurnRef.current && prev.length > 0 && prev[prev.length - 1].kind === 'assistant') {
              const updated = [...prev]
              const last = { ...updated[updated.length - 1] }
              last.text = last.text + ' ' + text
              updated[updated.length - 1] = last
              return updated
            }
            newAssistantTurnRef.current = false
            return [...prev, { kind: 'assistant', text }]
          })
          lastSpeakerRef.current = 'assistant'
        } else if (who === 'user') {
          lastUserTextRef.current = text
          userTurnsRef.current.push(text)
          setTranscript(prev => [...prev, { speaker: 'user', text, timestamp: message.timestamp }])
          // Timeline: user bubble
          setTimeline(prev => [...prev, { kind: 'user', text }])
          newAssistantTurnRef.current = true
          lastSpeakerRef.current = 'user'
        }
        break
      }

      case 'assistant_turn_boundary': {
        setAssistantTurnText(prev => prev ? `${prev}\n\n` : prev)
        newAssistantTurnRef.current = true
        lastSpeakerRef.current = 'user'
        break
      }

      case 'score': {
        const fromBackend = String(message.user_text || '').trim()
        const aligned = userTurnsRef.current.length ? userTurnsRef.current[userTurnsRef.current.length - 1] : ''
        const user_text = fromBackend || aligned || lastUserTextRef.current || ''
        const scoreObj = {
          overall: Number(message.overall),
          intent_match: Number(message.intent_match),
          fluency: Number(message.fluency),
          completeness: Number(message.completeness),
          vocabulary: Number(message.vocabulary),
          justification: String(message.justification ?? ''),
          user_text
        }
        setScores(prev => [...prev, scoreObj])
        // Timeline: inline score card
        setTimeline(prev => [...prev, { kind: 'score', score: scoreObj }])
        break
      }

      case 'goal_complete': {
        setGoals({ completed: message.completed, total: message.total })
        // Timeline: goal completion toast
        if (message.goal_name) {
          setTimeline(prev => [...prev, { kind: 'goal', text: message.goal_name }])
        }
        break
      }

      default:
        console.log('Unknown message:', message.type)
    }
  }

  // ---------- Audio capture → PCM16 to server ----------
  const startAudioCapture = () => {
    const ac = audioContextRef.current
    const stream = streamRef.current
    if (!ac || !stream) return

    const source = ac.createMediaStreamSource(stream)
    const processor = ac.createScriptProcessor(4096, 1, 1)

    processor.onaudioprocess = (e) => {
      if (uiConfigRef.current?.push_to_talk && !micActiveRef.current) return
      const now = Date.now()
      if (isPlayingRef.current || now - lastAssistantAudioAtRef.current < assistantAudioHoldMsRef.current) return
      if (!isRecordingRef.current || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return

      const inputData = e.inputBuffer.getChannelData(0)
      const downsampled = downsampleTo24k(inputData, ac.sampleRate)
      const pcm16 = floatTo16BitPCM(downsampled)
      wsRef.current.send(pcm16)
    }

    source.connect(processor)
    processor.connect(ac.destination)
    processorRef.current = processor
    isRecordingRef.current = true
    setIsRecording(true)
  }

  // ---------- Incoming audio (raw PCM16) ----------
  const handleIncomingPcm = async (arrayBuffer) => {
    const ac = audioContextRef.current
    if (!ac) return
    if (ac.state === 'suspended') {
      try { await ac.resume() } catch (e) { console.warn('Resume failed', e) }
    }
    lastAssistantAudioAtRef.current = Date.now()
    audioQueueRef.current.push(arrayBuffer)
    if (!isPlayingRef.current) playAudioQueue()
  }

  // Time-scheduled gapless playback
  const playAudioQueue = async () => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false
      maybeFinalizeAfterDrain()
      return
    }
    isPlayingRef.current = true
    const ac = audioContextRef.current
    if (!ac) { isPlayingRef.current = false; maybeFinalizeAfterDrain(); return }

    const arrayBuffer = audioQueueRef.current.shift()
    const pcm16 = new Int16Array(arrayBuffer)
    const float32 = new Float32Array(pcm16.length)
    for (let i = 0; i < pcm16.length; i++) float32[i] = pcm16[i] / 32768

    const buffer = ac.createBuffer(1, float32.length, 24000)
    buffer.getChannelData(0).set(float32)

    const src = ac.createBufferSource()
    src.buffer = buffer
    src.connect(ac.destination)

    const now = ac.currentTime
    const startAt = Math.max(now, nextPlayTimeRef.current)
    src.start(startAt)
    nextPlayTimeRef.current = startAt + buffer.duration

    const msUntilNext = Math.max(0, (startAt + buffer.duration - now) * 1000 - 20)
    setTimeout(() => playAudioQueue(), msUntilNext)
  }

  // ---------- Teardown ----------
  const stopMicCapture = () => {
    isRecordingRef.current = false
    setIsRecording(false)
    if (processorRef.current) { processorRef.current.disconnect(); processorRef.current = null }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null }
  }

  const stopRecording = () => {
    stopMicCapture()
    if (audioContextRef.current) { audioContextRef.current.close(); audioContextRef.current = null }
  }

  const maybeFinalizeAfterDrain = () => {
    if (!endRequestedRef.current) return
    if (audioQueueRef.current.length > 0 || isPlayingRef.current) return
    endRequestedRef.current = false
    assistantAudioDoneRef.current = false
    stopRecording()
    audioQueueRef.current = []
    isPlayingRef.current = false
    const next = pendingViewAfterDrainRef.current
    pendingViewAfterDrainRef.current = null
    if (next) setView(next)
  }

  const requestGracefulEnd = (nextView = 'complete') => {
    if (endRequestedRef.current) return
    endRequestedRef.current = true
    pendingViewAfterDrainRef.current = nextView
    assistantAudioDoneRef.current = false
    stopMicCapture()
    try {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'stop' }))
      }
    } catch (e) { console.warn('Stop send error:', e) }
    maybeFinalizeAfterDrain()
  }

  const endConversation = () => requestGracefulEnd('complete')

  // Push-to-talk toggle
  const togglePTT = () => {
    if (micActive) {
      setMicActive(false)
      try {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'commit' }))
        }
      } catch (e) { console.warn('PTT commit failed:', e) }
    } else {
      setMicActive(true)
    }
  }

  // Local file handler
  const handleLocalFiles = async (event) => {
    setError(null)
    const files = Array.from(event.target.files || [])
    const jsonFiles = files.filter(f => f.name.endsWith('.json'))
    if (jsonFiles.length === 0) { setError('No .json files found.'); return }

    const loaded = []
    for (const file of jsonFiles) {
      try {
        const text = await file.text()
        const json = JSON.parse(text)
        const id = file.name.replace('.json', '')
        if (json.title) loaded.push({ ...json, id })
      } catch (e) { console.warn(`Failed: ${file.name}`, e) }
    }

    if (loaded.length > 0) {
      setLessons(loaded)
      setSelectedLesson(loaded[0].id)
    } else {
      setError('No valid lesson files found.')
    }
  }

  // ---------- RENDER ----------
  const lessonTitle = lessons.find(l => l.id === selectedLesson)?.title || selectedLesson

  // Select view
  if (view === 'select') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <NavBar onLogout={handleLogout} mode={mode} />
        <LessonSelect
          lessons={lessons}
          lessonSource={lessonSource}
          selectedLesson={selectedLesson}
          selectedLevel={selectedLevel}
          mode={mode}
          error={error}
          onSelectLesson={setSelectedLesson}
          onSelectLevel={setSelectedLevel}
          onSelectSource={setLessonSource}
          onSelectMode={setMode}
          onLocalFiles={handleLocalFiles}
          onStart={startConversation}
          onClearError={() => setError(null)}
          isConnecting={isConnecting}
        />
      </div>
    )
  }

  // Conversation view
  if (view === 'conversation') {
    return (
      <div className="sqa-conversation">
        <NavBar
          lessonTitle={lessonTitle}
          levelCode={mode === 'test' ? 'TEST' : selectedLevel}
          mode={mode}
          onEnd={endConversation}
          onLogout={handleLogout}
        />
        <ConversationView
          lessonTitle={lessonTitle}
          timeline={timeline}
          assistantText={assistantTurnText}
          transcript={transcript}
          scores={scores}
          goals={goals}
          goalKeys={goalKeys}
          uiConfig={uiConfig}
          micActive={micActive}
          isRecording={isRecording}
          onTogglePTT={togglePTT}
          mode={mode}
          placedLevel={placedLevel}
          testScores={testScores}
          error={error}
        />
      </div>
    )
  }

  // Review view
  if (view === 'review') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <NavBar onLogout={handleLogout} mode={mode} />
        <ReviewView
          scores={scores}
          transcript={transcript}
          index={reviewIdx}
          setIndex={setReviewIdx}
          onDone={() => setView('complete')}
        />
      </div>
    )
  }

  // Complete view
  if (view === 'complete') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <NavBar onLogout={handleLogout} mode={mode} />
        <CompleteView
          mode={mode}
          goals={goals}
          scores={scores}
          testScores={testScores}
          placedLevel={placedLevel}
          serverExchanges={serverExchanges}
          transcript={transcript}
          onPracticeAgain={() => {
            // 1. Invalidate current session generation FIRST so any in-flight
            //    onclose/onerror from the old WS returns immediately without
            //    touching the new session's AudioContext.
            sessionGenRef.current++
            console.log(`🔄 Practice Again — invalidated to gen ${sessionGenRef.current}`)

            // 2. Close and null the old WS so the "session already active" guard
            //    in startConversation doesn't silently block the next attempt.
            try { if (wsRef.current) wsRef.current.close() } catch (e) { /* ignore */ }
            wsRef.current = null

            // 3. Stop any lingering audio / mic
            stopRecording()

            // 4. Reset all connection refs
            isConnectingRef.current = false
            setIsConnecting(false)
            endRequestedRef.current = false
            pendingViewAfterDrainRef.current = null
            audioQueueRef.current = []
            isPlayingRef.current = false
            nextPlayTimeRef.current = 0
            assistantAudioDoneRef.current = false

            // 5. Reset all display state
            setView('select')
            setTranscript([])
            setScores([])
            setTimeline([])
            setGoals({ completed: 0, total: 0 })
            setGoalKeys([])
            setServerExchanges(null)
            setAssistantTurnText('')
            setUiConfig(null)
            setMicActive(false)
            setTestScores([])
            setPlacedLevel(null)
            setError(null)
            lastSpeakerRef.current = null
            lastUserTextRef.current = ''
            userTurnsRef.current = []
            newAssistantTurnRef.current = true
          }}
          onReviewScores={() => { setReviewIdx(0); setView('review') }}
          onLogout={handleLogout}
        />
      </div>
    )
  }

  return null
}
